import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Member } from '../types';

export const FIRST_NAME = 'firstName';
export const LAST_NAME = 'lastName';
export const EMAIL = 'email';
export const ORGANIZATION_PERMISSIONS = 'organizationPermissions';
export const ORG_ROLE = 'orgRole'; // Client-side field (replaced by orgAdmin in payload)
export const ORG_ADMIN = 'orgAdmin'; // API field
export const USER_ID = 'userId';

export enum OrgRole {
  ADMIN = 'admin',
  MEMBER = 'member',
}

const createSchema = z.object({
  [FIRST_NAME]: z.string().min(1, 'Enter a first name'),
  [LAST_NAME]: z.string().min(1, 'Enter a last name'),
  [EMAIL]: z.string().min(1, 'Enter an email').email('Enter a valid email'),
  [ORG_ROLE]: z.nativeEnum(OrgRole).default(OrgRole.MEMBER),
  [ORGANIZATION_PERMISSIONS]: z.array(z.number()).default([]),
});

const editSchema = createSchema.extend({
  [USER_ID]: z.string(),
});

export type CreateMemberFormSchema = Required<z.infer<typeof createSchema>>;

export type EditMemberFormSchema = Required<z.infer<typeof editSchema>>;

export type MemberFormSchema = CreateMemberFormSchema | EditMemberFormSchema;

const emptyValues = {
  [FIRST_NAME]: '',
  [LAST_NAME]: '',
  [EMAIL]: '',
  [ORGANIZATION_PERMISSIONS]: [],
  [ORG_ROLE]: OrgRole.MEMBER,
};

const createDefaultValues = (member?: Member) => {
  if (!member) return emptyValues;
  return {
    [USER_ID]: member.userId,
    [FIRST_NAME]: member.firstName,
    [LAST_NAME]: member.lastName,
    [EMAIL]: member.email,
    [ORGANIZATION_PERMISSIONS]: member.organizationPermissions,
    [ORG_ROLE]: member.orgAdmin ? OrgRole.ADMIN : OrgRole.MEMBER,
  };
};

export const createMemberPayload = (values: MemberFormSchema) => {
  const { [ORG_ROLE]: role, ...rest } = values;
  /** orgAdmin value must be derived from the client-side orgRole form field */
  return { ...rest, [ORG_ADMIN]: role === OrgRole.ADMIN };
};

export const MemberFormProvider = ({
  children,
  member,
}: {
  children: React.ReactNode;
  member?: Member;
}) => {
  const defaultValues = createDefaultValues(member);
  const schema = member ? editSchema : createSchema;

  const methods = useForm<MemberFormSchema>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
